import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import styles from './assets/css/LoginPage.module.css';
import dhaLogo from '../assets/img/dha-logo.png';
import imagingSuiteImage from '../assets/img/united-imaging-healthcare.jpg';
import ctMachineImage from '../assets/img/the-medical-equipment-ct-machine-in-the-white-empty-room-3d-rendering-photo.jpg';

/**
 * LoginPage Component
 * User login with username and password
 */
const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const currentYear = new Date().getFullYear();

  // If already authenticated, redirect to dashboard
  if (isAuthenticated) {
    navigate('/dashboard');
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const result = await login(username, password);
      
      if (result.success) {
        // Redirect to dashboard or requested page
        const from = location.state?.from?.pathname || '/dashboard';
        navigate(from);
      } else {
        setError(result.error || 'Login failed');
      }
    } catch (err) {
      setError(err.message || 'An error occurred during login');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.loginContainer}>
      <section className={styles.formPane} aria-label="Login">
        <div className={styles.loginBox}>
          <div className={styles.header}>
            <img src={dhaLogo} alt="Digital Health Agency" className={styles.logo} />
            <h1>VEMS - Equipment</h1>
            <p>Gateway</p>
          </div>

          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.formGroup}>
              <label htmlFor="username">Username</label>
              <input
                type="text"
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your username"
                required
                disabled={isLoading}
                className={styles.input}
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                disabled={isLoading}
                className={styles.input}
              />
            </div>

            {error && <div className={styles.error}>{error}</div>}

            <button
              type="submit"
              disabled={isLoading}
              className={styles.submitBtn}
            >
              {isLoading ? 'Logging in...' : 'Login'}
            </button>
          </form>

          <Link to="/facility-lookup" className={styles.publicLookupLink}>
            Search facility equipment connection details
          </Link>

          <div className={styles.footer}>
            <p>&copy; {currentYear} Digital Health Agency. All rights reserved.</p>
          </div>
        </div>
      </section>

      <section className={styles.imagePane} aria-label="Medical equipment">
        <div className={styles.imagePanel}>
          <img src={imagingSuiteImage} alt="Modern medical imaging suite" />
        </div>
        <div className={styles.imagePanel}>
          <img src={ctMachineImage} alt="CT machine in a clinical room" />
        </div>
      </section>
    </div>
  );
};

export default LoginPage;
